import { Tab, Tabs } from '@mui/material';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import HeaderLogin from 'components/Header/header-items/HeaderLogin/HeaderLogin';
import InfiniteScroll from 'components/InfiniteScroll/InfiniteScroll';
import BlockImage from 'components/layout/BlockImages/BlockImage/BlockImage';
import BlockImages from 'components/layout/BlockImages/BlockImages';
import LikesModal from 'components/LikesModal';
import Loader from 'components/Loader/Loader';
import NotFound from 'components/NotFound/NotFound';
import OptionedMenu from 'components/OptionedMenu/OptionedMenu';
import ShopUserProfileFeedback from 'components/Shop/ShopUserProfile/Feedback/ShopUserProfileFeedback';
import { ShopFeedbackTab } from 'components/ShopFeedbackTab/ShopFeedbackTab';
import { PRODUCT } from 'config/fe-routes';
import useGlobalContext from 'hooks/useGlobalContext';
import { useLanguage } from 'hooks/useLanguage';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import MyShopPage from 'pages/[shop-slug]/my-shop';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LikeService from 'services/like.service';
import ProductService from 'services/product.service';
import ShopService from 'services/shop.service';
import { openLoginModal, setLoginModalStoreData } from 'store/actions/login.actions';
import { setShopFollowState, syncShopFollowState } from 'store/actions/shop-follow.actions';
import { useAppSelector } from 'store/hooks';
import { ProductType } from 'types/product.types';
import { ShopType } from 'types/shop.types';
import { buildCanonicalUrl } from 'utils/canonical-url';
import ImageUrlResizer from 'utils/image-url-resizer';
import { getShopSEOFriendlyTitle } from 'utils/seo-data';
import { formatServerSideProps } from 'utils/server-side-props';
import { useNotification } from 'utils/snackbar';

const shopService = new ShopService();
const productService = new ProductService();
const likeService = new LikeService();

type Props = {
  shopSlug: string;
  initialShop: ShopType;
  initialProducts: ProductType[];
};

const ITEMS_PER_ROW = 4;
const ITEMS_PER_PAGE = ITEMS_PER_ROW * 8;

type ShopHeaderProps = {
  shop?: ShopType;
  profileImage?: string;
  isScrolled?: boolean;
};

const ShopHeader = ({ shop, profileImage, isScrolled }: ShopHeaderProps) => (
  <Header position="sticky" disableOffsetAdjustment>
    {[
      [
        <HeaderIconLogo showLogo={false} icon="back" key="header-icon-logo" />,
        <div key="compact-profile" className={`shop-header-compact-profile${isScrolled ? ' shop-header-compact-profile--visible' : ''}`}>
          {profileImage && <img src={profileImage} alt="" className="shop-header-compact-profile__image" />}
          <span className="shop-header-compact-profile__name">{shop?.name}</span>
        </div>,
        <HeaderLogin key="header-login" />,
      ],
    ]}
  </Header>
);

const ShopPage = (props: Props) => {
  const { shopSlug, initialShop, initialProducts } = props;
  const { asPath, query, isReady, route } = useRouter();
  const user = useAppSelector(({ userData }) => userData?.user);
  const isUserLoading = useAppSelector(({ userData }) => userData?.isLoading);

  const [shopSeoTitle, setShopSeoTitle] = useState<string>('');

  const { t } = useTranslation();
  const { get, set } = useGlobalContext();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { locale } = useLanguage();

  const cacheKey = get('shopPageCacheKey') as string;
  const setCacheKey = (shopPageCacheKey) => set({ shopPageCacheKey });
  const isCacheApplied = getNewCacheKey() === cacheKey;

  const soldProducts = isCacheApplied ? (get('shopPageSoldProducts') as ProductType[]) : [];
  const setSoldProducts = (shopPageSoldProducts) => set({ shopPageSoldProducts });

  const hasMoreSoldProducts = get('shopPageHasMoreSoldProducts') as boolean;
  const setHasMoreSoldProducts = (shopPageHasMoreSoldProducts) => set({ shopPageHasMoreSoldProducts });

  const isLoading = get('shopPageIsLoading') as boolean;
  const setIsLoading = (shopPageIsLoading) => set({ shopPageIsLoading });

  const onClickShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showSuccessNotification(t('common.link-copied'));
  };

  const hasSSRProducts = initialProducts?.length > 0;

  // Use SSR products as fallback when cache doesn't match (fresh page load)
  const availableProductsFallback = hasSSRProducts ? initialProducts : [];
  const availableProducts = isCacheApplied ? (get('shopPageAvailableProducts') as ProductType[]) : availableProductsFallback;
  const setAvailableProducts = (shopPageAvailableProducts) => set({ shopPageAvailableProducts });

  const hasMoreAvailableProducts = isCacheApplied
    ? (get('shopPageHasMoreAvailableProducts') as boolean)
    : hasSSRProducts
      ? initialProducts.length >= ITEMS_PER_PAGE
      : true;
  const setHasMoreAvailableProducts = (shopPageHasMoreAvailableProducts) => set({ shopPageHasMoreAvailableProducts });

  const [wasCachedOnInit, setWasCachedOnInit] = useState(isCacheApplied || hasSSRProducts);

  const [shop, setShop] = useState(initialShop);
  const [isLikesModalOpen, setLikesModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'items' | 'reviews'>('items');
  // Automatic reviews already reported as shown in this shop visit. Owned here because the Reviews tab unmounts on Items.
  const trackedAutomaticReviewIds = useMemo(() => new Set<number>(), [shop?.id]);
  const [reviewsForceMinHeight, setReviewsForceMinHeight] = useState(false);
  const [isProfileScrolledOut, setIsProfileScrolledOut] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(60);
  const tabSwitcherRef = useRef<HTMLDivElement>(null);
  const [profileEl, setProfileEl] = useState<HTMLDivElement | null>(null);
  const tabScrollPositions = useRef<{ items: number | null; reviews: number | null }>({ items: null, reviews: null });
  const pendingScrollTarget = useRef<number | null>(null);
  const minHeightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stickyActivationYRef = useRef<number | null>(null);

  useEffect(() => {
    if (isReady && query.tab === 'reviews') {
      setActiveTab('reviews');
    }
  }, [isReady, query.tab]);

  useEffect(() => {
    const headerEl = document.getElementById('global-header');

    if (!headerEl) {
      return () => {
        // Do nothing.
      };
    }

    const update = () => setHeaderHeight(headerEl.offsetHeight);

    update();

    window.addEventListener('resize', update);

    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (!profileEl) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setIsProfileScrolledOut(!entry.isIntersecting), { threshold: 0 });

    observer.observe(profileEl);

    return () => observer.disconnect();
  }, [profileEl]);

  useEffect(() => {
    if (initialShop) {
      setShop(initialShop);
    }
  }, [initialShop, setShop]);

  useEffect(() => {
    const updateStickyActivationY = () => {
      if (!tabSwitcherRef.current) {
        return;
      }

      const rect = tabSwitcherRef.current.getBoundingClientRect();

      if (rect.top > headerHeight + 2) {
        stickyActivationYRef.current = rect.top + window.scrollY - headerHeight;
      }
    };

    updateStickyActivationY();
    window.addEventListener('scroll', updateStickyActivationY, { passive: true });

    return () => window.removeEventListener('scroll', updateStickyActivationY);
  }, [headerHeight]);

  useEffect(() => {
    if (isLoading || stickyActivationYRef.current !== null || !tabSwitcherRef.current) {
      return;
    }

    const rect = tabSwitcherRef.current.getBoundingClientRect();

    if (rect.top > headerHeight + 2) {
      stickyActivationYRef.current = rect.top + window.scrollY - headerHeight;
    }
  }, [isLoading, headerHeight]);

  useLayoutEffect(() => {
    if (pendingScrollTarget.current === null) {
      return;
    }

    window.scrollTo(0, pendingScrollTarget.current);
    pendingScrollTarget.current = null;

    if (minHeightTimerRef.current) {
      clearTimeout(minHeightTimerRef.current);
    }
    minHeightTimerRef.current = setTimeout(() => {
      document.documentElement.style.minHeight = '';
    }, 1500);
  }, [activeTab]);

  // Seed global context cache with SSR products so back-navigation preserves them
  useEffect(() => {
    if (!isCacheApplied && hasSSRProducts) {
      setCacheKey(shopSlug);
      setAvailableProducts(initialProducts);
      setHasMoreAvailableProducts(initialProducts.length >= ITEMS_PER_PAGE);
      setSoldProducts([]);
      setHasMoreSoldProducts(true);
    }
  }, [shopSlug]);

  useEffect(() => {
    if (shop?.owner?.id && typeof shop.owner.hasLiked === 'boolean') {
      syncShopFollowState({ [shop.owner.id]: shop.owner.hasLiked });
    }
  }, [shop?.owner?.id, shop?.owner?.hasLiked]);

  // Process localStorage like data once on mount (handles post-login redirect)
  useEffect(() => {
    (async () => {
      const storedLikeData = await likeService.getLocalStorageLike();

      setShopLiked(storedLikeData);
    })();
  }, []);

  function getNewCacheKey(): string {
    return shopSlug;
  }

  const updateShopLikeData = (liked: boolean) => {
    const currentLikeCount = shop?.owner?.likeCount ?? 0;

    if (shop?.owner?.id) {
      setShopFollowState(shop.owner.id, liked);
    }

    setShop({
      ...shop,
      owner: {
        ...shop.owner,
        hasLiked: liked,
        likeCount: liked ? currentLikeCount + 1 : Math.max(0, currentLikeCount - 1),
      },
    });
  };

  const toggleLike = async () => {
    if (shop) {
      const likeData = { liked: !shop.owner?.hasLiked, id: shop?.owner?.id };

      if (!user?.isLoggedIn) {
        setLoginModalStoreData({ ...likeData, type: LikeService.USER_TYPE });
        openLoginModal(true);

        return;
      }
      setShopLiked(likeData);
    }
  };

  const setShopLiked = async (likedData) => {
    if (!likedData) {
      return;
    }

    try {
      const response = await likeService.changeLikeStatus(likedData, 'userService');

      updateShopLikeData(likedData.liked);
      if (response?.showLikeNotification) {
        showSuccessNotification(t('pages.shop.likes-modal.like-notification'));
      }
    } catch (error) {
      if (error?.data?.data?.msg !== 'Shop not found') {
        showErrorNotification({ error });
      }
    }
  };

  // New search request when search key or filters change.
  const infiniteScrollBeforeEachLoad = ({ reset }) => {
    const newProductCacheKey = getNewCacheKey();

    // If url changed (cache key), make new request.
    if (newProductCacheKey !== cacheKey) {
      setCacheKey(newProductCacheKey);
      reset();

      // When SSR products are available, seed the cache instead of re-fetching page 0
      if (hasSSRProducts) {
        setAvailableProducts(initialProducts);
        setHasMoreAvailableProducts(initialProducts.length >= ITEMS_PER_PAGE);
      } else {
        setAvailableProducts([]);
        setHasMoreAvailableProducts(true);
        loadMoreAvailableProducts(0);
      }

      setSoldProducts([]);
      setHasMoreSoldProducts(true);
      window.scrollTo(0, 0);

      // Stop loading next page this time.
      return true;
    }
  };

  useEffect(() => {
    setIsLoading(false);
    if (shop) {
      setShopSeoTitle(getShopSEOFriendlyTitle(shop));
    }
  }, [shop]);

  // Load next set of published products
  const loadMoreAvailableProducts = async (page: number) => {
    try {
      const { list: nextProducts, total } = await productService.getProducts({
        shopId: shop.id,
        status: 'published',
        offset: ITEMS_PER_PAGE * page,
        limit: ITEMS_PER_PAGE,
      });

      // Combine available products
      const combinedAvailableProducts = availableProducts.concat(nextProducts || []);

      // Check for more
      setHasMoreAvailableProducts(!!nextProducts?.length && total > combinedAvailableProducts.length);

      // Save published products
      setAvailableProducts(combinedAvailableProducts);
    } catch (e) {
      // Do nothing
    }

    // After we load one page we can safely assume that next loaded items are not part of cache.
    // This will enable blockimage loader and lazyness.
    setWasCachedOnInit(false);
  };

  // Load next set of sold products
  const loadMoreSoldProducts = async (page: number) => {
    try {
      const { list: nextProducts, total } = await productService.getProducts({
        shopId: shop.id,
        status: 'sold',
        offset: ITEMS_PER_PAGE * page,
        limit: ITEMS_PER_PAGE,
      });

      // Combine data
      const combinedSoldProducts = soldProducts.concat(nextProducts || []);

      // Check for more
      setHasMoreSoldProducts(!!nextProducts?.length && total > combinedSoldProducts.length);

      // Save sold products
      setSoldProducts(combinedSoldProducts);
    } catch (e) {
      // Do nothing
    }

    // After we load one page we can safely assume that next loaded items are not part of cache.
    // This will enable blockimage loader and lazyness.
    setWasCachedOnInit(false);
  };

  const reloadShop = async (skipLoading?: boolean, disableCache?: boolean) => {
    if (!skipLoading) {
      setIsLoading(true);
    }
    try {
      const refreshedShopData = await shopService.getShop(
        shopSlug,
        disableCache
          ? {
              headers: {
                'Cache-Control': 'no-cache',
              },
              params: {
                _t: Date.now() + Math.random(),
              },
            }
          : {},
      );

      setShop(refreshedShopData);
    } catch (e) {
      // Do nothing
    }

    if (!skipLoading) {
      setIsLoading(false);
    }
  };

  const quietReloadShopNoCache = () => reloadShop(true, true);

  const renderShopBySlug = () => (
    <>
      <ShopHeader shop={shop} profileImage={ImageUrlResizer.resize(shop?.owner?.picture, 100)} isScrolled={isProfileScrolledOut} />

      <div className="shop-page feedback-enabled">
        {shop?.ownerId !== user?.id && (
          <div className="shop-tab-switcher-actions" style={{ top: headerHeight }}>
            <div className="shop-tab-switcher-actions__buttons">
              <OptionedMenu
                user={user}
                targetUser={shop?.owner}
                targetShop={shop}
                refreshParentDataFn={quietReloadShopNoCache}
                onClickShare={onClickShare}
              />
            </div>
          </div>
        )}

        {
          <>
            <ShopUserProfileFeedback
              shop={shop}
              currentUser={user}
              profileImage={shop?.owner?.picture}
              likeCount={shop?.owner?.likeCount}
              lastActive={shop?.owner?.lastActive}
              orderCount={shop?.completedOrdersCount}
              likeShop={() => setLikesModalOpen(true)}
              refreshParentDataFn={quietReloadShopNoCache}
              likeCallback={toggleLike}
              imageRef={setProfileEl}
              onRatingClick={() => {
                tabScrollPositions.current['items'] = window.scrollY;
                setReviewsForceMinHeight(true);
                setActiveTab('reviews');
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    window.scrollTo({ top: stickyActivationYRef.current ?? 0, behavior: 'smooth' });
                  });
                });
              }}
            />
          </>
        }

        <div className="shop-tab-switcher" ref={tabSwitcherRef} style={{ top: headerHeight }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue: 'items' | 'reviews') => {
              const stickyActivationY = stickyActivationYRef.current ?? 0;
              const isActiveStickyZone = window.scrollY >= stickyActivationY;
              const inactiveStored = tabScrollPositions.current[newValue];
              const isInactiveStickyZone = inactiveStored !== null && inactiveStored >= stickyActivationY;

              tabScrollPositions.current[activeTab] = window.scrollY;

              let targetScroll: number;

              if (!isActiveStickyZone && !isInactiveStickyZone) {
                // Both tabs in pre-sticky zone → stay at current scroll (scenario 2)
                targetScroll = window.scrollY;
              } else if (isInactiveStickyZone) {
                // Inactive tab has a sticky-zone position → restore it
                targetScroll = inactiveStored as number;
              } else {
                // Active was sticky but inactive has no sticky-zone position → top of sticky zone
                targetScroll = stickyActivationY;
              }

              if (newValue === 'reviews') {
                setReviewsForceMinHeight(isActiveStickyZone);
              }
              document.documentElement.style.minHeight = `${document.documentElement.scrollHeight}px`;
              pendingScrollTarget.current = targetScroll;
              setActiveTab(newValue);
            }}
            TabIndicatorProps={{
              style: {
                height: '40px',
                borderRadius: '21px',
                backgroundColor: '#ffffff',
                bottom: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              },
            }}
          >
            <Tab value="items" label={t('components.page-shop.tab-items')} disableRipple />
            <Tab value="reviews" label={t('components.page-shop.tab-reviews')} disableRipple />
          </Tabs>
        </div>

        {/* products */}
        {activeTab === 'items' && (
          <BlockImages>
            {/* available products */}
            <InfiniteScroll
              dataLength={availableProducts.length}
              batchSize={ITEMS_PER_PAGE}
              loadMore={(page) => loadMoreAvailableProducts(page)}
              hasMore={hasMoreAvailableProducts}
              loader={<Loader />}
              manualLoadFirstSet
              beforeEachLoad={infiniteScrollBeforeEachLoad}
            >
              {availableProducts?.map((product, index) => {
                const productImage = product?.images?.[0];

                return (
                  <BlockImage
                    key={'shop-available-product-key-' + product?.id}
                    isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                    src={ImageUrlResizer.resize(productImage.original, 600, true)}
                    alt={productImage?.title}
                    price={product.price}
                    priceDrop={product.priceDrop}
                    productSlug={product.slug}
                    status={product.status}
                    details={product.size}
                    brand={product.brand}
                    tagged={product.tagged}
                    md={3}
                    globalLoaderEnabled={true}
                    showImageLoader={!wasCachedOnInit}
                    disableLazy={wasCachedOnInit}
                    productId={product.id}
                    shopId={product.shopId}
                    likeCount={product.likeCount}
                    isNewLister={shop?.isNewLister}
                    {...PRODUCT(shop?.active_slug, product.slug, undefined, index)}
                  />
                );
              })}
            </InfiniteScroll>

            {/* sold products - load them when no more published products available */}
            {!hasMoreAvailableProducts && (
              <InfiniteScroll
                dataLength={soldProducts.length}
                batchSize={ITEMS_PER_PAGE}
                loadMore={(page) => loadMoreSoldProducts(page)}
                hasMore={hasMoreSoldProducts}
                loader={<Loader />}
              >
                {soldProducts.map((product, index) => {
                  const productImage = product?.images?.[0];

                  return (
                    <BlockImage
                      key={'shop-sold-product-key-' + product?.id}
                      src={ImageUrlResizer.resize(productImage.original, 600, true)}
                      isCharity={product?.tags?.includes('Charity') || product?.isCharityProduct}
                      alt={productImage?.title}
                      price={product.price}
                      status={product.status}
                      details={product.size}
                      brand={product.brand}
                      tagged={product.tagged}
                      md={3}
                      globalLoaderEnabled={true}
                      showImageLoader={!wasCachedOnInit}
                      disableLazy={wasCachedOnInit}
                      productId={product.id}
                      shopId={product.shopId}
                      likeCount={product.likeCount}
                      isNewLister={shop?.isNewLister}
                      {...PRODUCT(shop?.active_slug, product.slug, undefined, index)}
                    />
                  );
                })}
              </InfiniteScroll>
            )}
          </BlockImages>
        )}

        {activeTab === 'reviews' && (
          <ShopFeedbackTab
            shop={shop}
            isOpen={activeTab === 'reviews'}
            setOpen={() => setActiveTab('items')}
            feedbackReceiver={{
              ...shop.owner,
              picture: shop?.owner?.picture,
              shop,
            }}
            refresh={quietReloadShopNoCache}
            forceMinHeight={reviewsForceMinHeight}
            trackedAutomaticReviewIds={trackedAutomaticReviewIds}
          />
        )}

        {/* NO PRODUCTS */}
        {!hasMoreAvailableProducts && !hasMoreSoldProducts && !availableProducts.length && !soldProducts.length && (
          <div className="mt-6 text-soft text-align-center">
            <NotFound target="products" customMessage="pages.sinupood.not-found" title="" />
          </div>
        )}

        {/* LIKES MODAL */}
        <LikesModal
          isOpen={isLikesModalOpen}
          isLiked={shop?.owner?.hasLiked}
          likeCount={shop?.owner?.likeCount}
          targetId={shop?.owner_id}
          productOrShopSlug={shop?.activeSlug}
          currentUserId={user?.id}
          onLikeToggle={updateShopLikeData}
          onClose={() => {
            setLikesModalOpen(false);
          }}
        />
      </div>
    </>
  );

  const renderShop = () => {
    // Shop not found
    if (!shop?.id) {
      return (
        <>
          <ShopHeader />
          <div className="shop-page">
            <NotFound
              target="general"
              title={t('components.not-found.target.page')}
              icon={<img src="/icons/illustration-page-not-found.svg" alt="" style={{ marginBottom: '40px' }} />}
            />
          </div>
        </>
      );
    }

    if (user?.id && !isUserLoading) {
      if (user?.shop?.id === shop?.id) {
        // If it is logged in user's shop, render MyShop page instead
        return <MyShopPage shop={shop} reloadShop={quietReloadShopNoCache} />;
      }

      return renderShopBySlug();
    } else if (isUserLoading) {
      return <Loader />;
    } else {
      return renderShopBySlug();
    }
  };

  return (
    <>
      <NextSeo
        title={shopSeoTitle}
        openGraph={{
          title: shopSeoTitle,
          url: buildCanonicalUrl(route, query, asPath),
          description: shop?.description,
          images: [
            {
              url: initialProducts?.[0]?.images?.[0]?.gallery,
              width: 600,
              height: 600,
              alt: shop?.name,
            },
          ],
          type: 'profile',
          locale,
          profile: {
            firstName: shop?.owner?.first_name,
            lastName: shop?.owner?.last_name,
            username: shop?.owner?.shop?.active_slug,
          },
        }}
      />

      {!isLoading ? renderShop() : <Loader />}
    </>
  );
};

export default ShopPage;

export const getServerSideProps = async (ctx) => {
  const { query } = ctx;
  const shopSlug = query['shop-slug'];
  let initialShop = null;
  let initialProducts: ProductType[] = [];

  try {
    if (shopSlug) {
      initialShop = await shopService.getShop(shopSlug, {
        silentFail: true,
        ctx,
      });
    }
  } catch (e) {
    // Only a definitive API 404 may become a page 404 — transient failures
    // (5xx, timeouts) must render a 500 so crawlers retry instead of deindexing.
    if (e?.data?.status !== 404) {
      throw e;
    }
  }

  if (!initialShop) {
    return { notFound: true };
  }

  if (initialShop.id) {
    try {
      const products = await productService.getProducts({
        shopId: initialShop.id,
        status: 'published',
        offset: 0,
        limit: ITEMS_PER_PAGE,
      });

      initialProducts = products?.list || [];
    } catch (e) {
      // Do nothing
    }
  }

  return {
    props: formatServerSideProps({
      shopSlug,
      initialShop,
      initialProducts,
    }),
  };
};
